import {Card, DataStatus, Form, FormField} from 'platform/components';
import {Grid} from 'platform/foundation';

import {
  useGetCheckoutByIdQuery,
  usePatchCheckoutForeignCurrencyPaymentDisallowMutation,
  usePostCheckoutForeignCurrencyPaymentAllowMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {DEFAULT_FOREIGN_CURRENCY, handleApiError, useCurrencies} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

interface ForeginCurrencyPaymentCardProp extends RequiredTestIdProps {
  checkoutId: string;
  areAllPaymentsConcept: boolean;
  isEditingDisabled?: boolean;
}

export function ForeginCurrencyPaymentCard(props: ForeginCurrencyPaymentCardProp) {
  const {currencyOptions} = useCurrencies();

  const {
    data: checkoutData,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
  } = useGetCheckoutByIdQuery({checkoutId: props.checkoutId});

  const [postCheckoutForeignCurrencyPaymentAllow, {isLoading: isAllowingLoading}] =
    usePostCheckoutForeignCurrencyPaymentAllowMutation();
  const [atchCheckoutForeignCurrencyPaymentDisallow, {isLoading: isDisallowingLoading}] =
    usePatchCheckoutForeignCurrencyPaymentDisallowMutation();

  const handleSwitchForeginCurrency = (shouldAllow: boolean) => {
    if (shouldAllow) {
      return postCheckoutForeignCurrencyPaymentAllow({
        checkoutId: props.checkoutId,
        body: {exchangeRateRatio: 1, exchangeCurrency: DEFAULT_FOREIGN_CURRENCY},
      })
        .unwrap()
        .catch(handleApiError);
    }

    return atchCheckoutForeignCurrencyPaymentDisallow({
      checkoutId: props.checkoutId,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleSaveForm = (data: ForeignCurrencyForm) =>
    postCheckoutForeignCurrencyPaymentAllow({
      checkoutId: props.checkoutId,
      body: {exchangeRateRatio: 1, exchangeCurrency: data.currency},
    })
      .unwrap()
      .catch(handleApiError);

  return (
    <DataStatus isLoading={isCheckoutLoading} isError={isCheckoutError}>
      <Card
        control={{
          type: 'switch',
          onChange: handleSwitchForeginCurrency,
          value: !!checkoutData?.isForeignCurrencyPayment,
          isDisabled:
            isAllowingLoading ||
            isDisallowingLoading ||
            !props.areAllPaymentsConcept ||
            props.isEditingDisabled,
        }}
        title={i18n.t('general.labels.foreignCurrency')}
        variant="inlineGrey"
        isExpanded={checkoutData?.isForeignCurrencyPayment ?? false}
        isClosedByDefault
      >
        <Form<ForeignCurrencyForm>
          defaultValues={{
            currency: checkoutData?.foreignCurrencyPayment?.exchangeCurrency ?? undefined,
            exchangeRateRatioAmount:
              checkoutData?.foreignCurrencyPayment?.exchangeRateRatio ?? undefined,
          }}
          onChange={handleSaveForm}
          isDisabled={props.isEditingDisabled}
        >
          {(control) => (
            <Grid columns={4}>
              <FormField
                name="currency"
                label={i18n.t('general.labels.currency')}
                type="choice"
                options={currencyOptions}
                isNotClearable
                control={control}
                data-testid={suffixTestId('currency', props)}
              />
              <FormField
                name="exchangeRateRatioAmount"
                label={i18n.t('general.labels.amount')}
                type="number"
                isDisabled
                control={control}
                data-testid={suffixTestId('exchangeRateRatioAmount', props)}
              />
            </Grid>
          )}
        </Form>
      </Card>
    </DataStatus>
  );
}

type ForeignCurrencyForm = {
  currency: string;
  exchangeRateRatioAmount: number;
};
